import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  FormControl,
  FormHelperText,
  Paper,
  InputAdornment,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  Container,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Snackbar,
  Fade,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Link
} from "@mui/material";
import { 
  Visibility, 
  VisibilityOff, 
  Email, 
  Lock,
  Info,
  Person,
  Phone,
  VerifiedUser,
  Security,
  HelpOutline
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import GoogleLoginButton from "../components/GoogleLoginButton";

// API URL from environment
const apiUrl = process.env.REACT_APP_API_URL || "/api";
// This is a test key - in production, you would use your own site key
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI";

const SignIn = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const { login, verifyTwoFactor, error: authError } = useAuth();
  
  const [formData, setFormData] = useState({
    emailOrPhone: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const [loginError, setLoginError] = useState(""); 
  const [loading, setLoading] = useState(false);
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [showTwoFactorInput, setShowTwoFactorInput] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [twoFactorError, setTwoFactorError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockTimeRemaining, setBlockTimeRemaining] = useState(0);
  const [blockTimer, setBlockTimer] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // Check for success message from registration
  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
    }
    
    // Check if there are stored login attempts
    const storedAttempts = localStorage.getItem('loginAttempts');
    const storedBlockTime = localStorage.getItem('loginBlockUntil');
    
    if (storedAttempts) {
      setLoginAttempts(parseInt(storedAttempts));
    }
    
    if (storedBlockTime) {
      const blockUntil = parseInt(storedBlockTime);
      const now = new Date().getTime();
      
      if (blockUntil > now) {
        setIsBlocked(true);
        setBlockTimeRemaining(Math.ceil((blockUntil - now) / 1000));
        startBlockTimer(Math.ceil((blockUntil - now) / 1000));
      } else {
        // Block time has expired
        localStorage.removeItem('loginBlockUntil');
      }
    }
  }, [location]);

  // Function to start the block timer countdown
  const startBlockTimer = (seconds) => {
    if (blockTimer) {
      clearInterval(blockTimer);
    }
    
    const timer = setInterval(() => {
      setBlockTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setIsBlocked(false);
          localStorage.removeItem('loginBlockUntil');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    setBlockTimer(timer);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (blockTimer) {
        clearInterval(blockTimer);
      }
    };
  }, [blockTimer]);

  const validateForm = () => {
    let newErrors = {};
    if (!formData.emailOrPhone)
      newErrors.emailOrPhone = "Email or phone is required";
    if (!formData.password) newErrors.password = "Password is required";
    if (!recaptchaValue) newErrors.recaptcha = "Please complete the CAPTCHA verification";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateTwoFactor = () => {
    let valid = true;
    if (!twoFactorCode || twoFactorCode.length !== 6) {
      setTwoFactorError("Please enter a valid 6-digit code");
      valid = false;
    } else {
      setTwoFactorError("");
    }
    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError("");
    
    // Check if login is blocked
    if (isBlocked) {
      setLoginError(`Too many failed attempts. Please try again in ${formatBlockTime(blockTimeRemaining)}.`);
      return;
    }
    
    if (showTwoFactorInput) {
      if (!validateTwoFactor()) return;
      
      setLoading(true);
      try {
        await verifyTwoFactor(formData.emailOrPhone, twoFactorCode, rememberDevice);
        
        // Reset login attempts on successful login
        setLoginAttempts(0);
        localStorage.removeItem('loginAttempts');
        
        navigate("/");
      } catch (error) {
        setTwoFactorError(error.response?.data?.message || authError || "Invalid verification code");
        
        // Increment failed attempts for 2FA as well
        handleFailedLoginAttempt();
      } finally {
        setLoading(false);
      }
      return;
    }
    
    if (!validateForm()) return; // Exit if validation fails

    setLoading(true);
    try {
      const user = await login(formData.emailOrPhone, formData.password);
      
      // Check if 2FA is required
      if (user.requireTwoFactor) {
        setShowTwoFactorInput(true);
        setCurrentUser(user);
        // The backend should have sent a code to the user's email or phone
      } else {
        // No 2FA required, proceed with login
        // Reset login attempts on successful login
        setLoginAttempts(0);
        localStorage.removeItem('loginAttempts');
        
        navigate("/");
      }
    } catch (error) {
      handleFailedLoginAttempt();
      setLoginError(error.response?.data?.message || authError || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  // Handle failed login attempt
  const handleFailedLoginAttempt = () => {
    const newAttempts = loginAttempts + 1;
    setLoginAttempts(newAttempts);
    localStorage.setItem('loginAttempts', newAttempts.toString());
    
    // Block login after 5 failed attempts
    if (newAttempts >= 5) {
      const blockDuration = 5 * 60 * 1000; // 5 minutes in milliseconds
      const blockUntil = new Date().getTime() + blockDuration;
      
      localStorage.setItem('loginBlockUntil', blockUntil.toString());
      setIsBlocked(true);
      setBlockTimeRemaining(blockDuration / 1000);
      startBlockTimer(blockDuration / 1000);
      
      setLoginError(`Too many failed attempts. Your account is temporarily blocked for ${formatBlockTime(blockDuration / 1000)}.`);
    }
  };

  // Format block time for display
  const formatBlockTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleRecaptchaChange = (value) => {
    setRecaptchaValue(value);
    if (value) {
      setErrors({...errors, recaptcha: undefined});
    }
  };

  const handleTwoFactorChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
    setTwoFactorCode(value);
    if (twoFactorError && value.length === 6) {
      setTwoFactorError("");
    }
  };

  const handleCloseSuccessMessage = () => {
    setSuccessMessage("");
  };

  const handleRememberDeviceChange = (e) => {
    setRememberDevice(e.target.checked);
  };

  return (
    <Box
      sx={{
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        minHeight: "calc(100vh - 120px)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: { xs: "20px", sm: "40px" },
      }}
    >
      <Container maxWidth="sm">
        <Card
          elevation={3}
          sx={{
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 8px 40px rgba(0,0,0,0.12)",
            transform: "translateY(0)",
            transition: "transform 0.3s ease-in-out",
            "&:hover": {
              transform: "translateY(-5px)",
            }
          }}
          className="slide-in-up"
        >
          <Box
            sx={{
              background: "linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)",
              padding: "30px 20px",
              textAlign: "center",
              color: "white",
            }}
          >
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
              {showTwoFactorInput ? "Two-Factor Authentication" : "Welcome Back"}
            </Typography>
            <Typography variant="subtitle1">
              {showTwoFactorInput 
                ? "Please enter the verification code sent to your device" 
                : "Sign in to access your account"}
            </Typography>
          </Box>
          
          <CardContent sx={{ padding: { xs: "20px", sm: "30px" } }}>
            {loginError && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3,
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}
              >
                {loginError}
              </Alert>
            )}
            
            {successMessage && (
              <Snackbar
                open={!!successMessage}
                autoHideDuration={6000}
                onClose={handleCloseSuccessMessage}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              >
                <Alert 
                  onClose={handleCloseSuccessMessage} 
                  severity="success"
                  sx={{ width: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}
                >
                  {successMessage}
                </Alert>
              </Snackbar>
            )}
            
            {isBlocked && (
              <Alert 
                severity="warning" 
                sx={{ 
                  mb: 3,
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}
              >
                <Typography variant="body2">
                  Your account is temporarily blocked due to too many failed login attempts.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                  Time remaining: {formatBlockTime(blockTimeRemaining)}
                </Typography>
              </Alert>
            )}
            
            <form onSubmit={handleSubmit}>
              {!showTwoFactorInput ? (
                // Regular login form
                <Grid container spacing={3}>
                  {/* Email or Phone */}
                  <Grid item xs={12}>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>Email or Phone</Typography>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        border: '1px solid rgba(0, 0, 0, 0.23)', 
                        borderRadius: '4px',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                        },
                        ...(errors.emailOrPhone && {
                          borderColor: theme.palette.error.main,
                        })
                      }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          p: 1.5,
                          color: 'action.active'
                        }}>
                          <Email />
                        </Box>
                        <input
                          type="text"
                          value={formData.emailOrPhone}
                          onChange={(e) => handleChange({
                            target: {
                              name: 'emailOrPhone',
                              value: e.target.value
                            }
                          })}
                          style={{
                            flex: 1,
                            border: 'none',
                            outline: 'none',
                            padding: '10px 0',
                            fontSize: '16px',
                            fontFamily: 'inherit',
                            background: 'transparent'
                          }}
                          placeholder="Enter your email or phone"
                          disabled={isBlocked}
                        />
                      </Box>
                      {errors.emailOrPhone && (
                        <Typography variant="caption" color="error.main" sx={{ mt: 0.5, display: 'block' }}>
                          {errors.emailOrPhone}
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  {/* Password */}
                  <Grid item xs={12}>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>Password</Typography>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        border: '1px solid rgba(0, 0, 0, 0.23)', 
                        borderRadius: '4px',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                        },
                        ...(errors.password && {
                          borderColor: theme.palette.error.main,
                        })
                      }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          p: 1.5,
                          color: 'action.active'
                        }}>
                          <Lock />
                        </Box>
                        <input
                          type={showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={(e) => handleChange({
                            target: {
                              name: 'password',
                              value: e.target.value
                            }
                          })}
                          style={{
                            flex: 1,
                            border: 'none',
                            outline: 'none',
                            padding: '10px 0',
                            fontSize: '16px',
                            fontFamily: 'inherit',
                            background: 'transparent'
                          }}
                          placeholder="Enter your password"
                          disabled={isBlocked}
                        />
                        <Box sx={{ pr: 1 }}>
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            edge="end"
                            disabled={isBlocked}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </Box>
                      </Box>
                      {errors.password && (
                        <Typography variant="caption" color="error.main" sx={{ mt: 0.5, display: 'block' }}>
                          {errors.password}
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  {/* ReCAPTCHA */}
                  <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <ReCAPTCHA
                      sitekey={RECAPTCHA_SITE_KEY}
                      onChange={handleRecaptchaChange}
                      disabled={isBlocked}
                    />
                  </Grid>
                  {errors.recaptcha && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="error" align="center" sx={{ display: 'block' }}>
                        {errors.recaptcha}
                      </Typography>
                    </Grid>
                  )}

                  {/* Remember Me & Forgot Password */}
                  <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={rememberDevice}
                          onChange={handleRememberDeviceChange}
                          color="primary"
                          size="small"
                          disabled={isBlocked}
                        />
                      }
                      label={
                        <Typography variant="body2">Remember me</Typography>
                      }
                    />
                    <Link component={RouterLink} to="/forgot-password" color="primary" underline="hover">
                      <Typography variant="body2">Forgot password?</Typography>
                    </Link>
                  </Grid>

                  {/* Login Button */}
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      fullWidth
                      size="large"
                      disabled={loading || isBlocked}
                      sx={{ 
                        py: 1.5,
                        mt: 1,
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(55, 125, 255, 0.2)',
                        '&:hover': {
                          boxShadow: '0 6px 16px rgba(55, 125, 255, 0.3)',
                        }
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        "Sign In"
                      )}
                    </Button>
                  </Grid>

                  {/* Divider */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        OR
                      </Typography>
                    </Divider>
                  </Grid>

                  {/* Google Sign-In */}
                  <Grid item xs={12}>
                    <GoogleLoginButton 
                      onError={(error) => setLoginError(error)}
                    />
                  </Grid>
                </Grid>
              ) : (
                // Two-factor authentication form
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <Security sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        A verification code has been sent to your {currentUser?.email ? 'email' : 'phone'}.
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Please enter the 6-digit code below.
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      flexDirection: 'column'
                    }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        border: '1px solid rgba(0, 0, 0, 0.23)', 
                        borderRadius: '4px',
                        width: '100%',
                        maxWidth: '300px',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                        },
                        ...(twoFactorError && {
                          borderColor: theme.palette.error.main,
                        })
                      }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          p: 1.5,
                          color: 'action.active'
                        }}>
                          <VerifiedUser />
                        </Box>
                        <input
                          type="text"
                          value={twoFactorCode}
                          onChange={handleTwoFactorChange}
                          style={{
                            flex: 1,
                            border: 'none',
                            outline: 'none',
                            padding: '10px 0',
                            fontSize: '16px',
                            fontFamily: 'inherit',
                            background: 'transparent',
                            textAlign: 'center',
                            letterSpacing: '0.5em'
                          }}
                          placeholder="000000"
                          maxLength={6}
                          disabled={isBlocked}
                        />
                      </Box>
                      {twoFactorError && (
                        <Typography variant="caption" color="error.main" sx={{ mt: 0.5, display: 'block' }}>
                          {twoFactorError}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={rememberDevice}
                          onChange={handleRememberDeviceChange}
                          color="primary"
                          size="small"
                          disabled={isBlocked}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2">Trust this device for 30 days</Typography>
                          <Tooltip title="This will reduce the frequency of 2FA prompts on this device">
                            <IconButton size="small" sx={{ ml: 0.5 }}>
                              <HelpOutline fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      fullWidth
                      size="large"
                      disabled={loading || isBlocked}
                      sx={{ 
                        py: 1.5,
                        mt: 1,
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(55, 125, 255, 0.2)',
                        '&:hover': {
                          boxShadow: '0 6px 16px rgba(55, 125, 255, 0.3)',
                        }
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        "Verify"
                      )}
                    </Button>
                  </Grid>
                </Grid>
              )}
              
              {/* Sign Up Link */}
              <Box sx={{ mt: 4, textAlign: "center" }}>
                <Typography variant="body2">
                  Don't have an account?{" "}
                  <Link component={RouterLink} to="/sign-up" color="primary">
                    Sign Up
                  </Link>
                </Typography>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default SignIn;
